import {
  AccountBalance,
  Business,
  DirectionsCar,
  Person,
  ThumbDown,
  ThumbUp,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Vehicle } from '@otw/models/inventory/inventory';
import React from 'react';
import {
  formatCurrency,
  formatNumber,
  getDealQualityLabel,
  getProjectedMileage,
  getVehicleSoldValue,
} from '../../util/customerUtils';

const dealColor = (
  label: 'Good' | 'Bad' | 'Unknown',
): 'success' | 'error' | 'default' => {
  if (label === 'Good') return 'success';
  if (label === 'Bad') return 'error';
  return 'default';
};

export type SoldVehicleLabelKind = 'company' | 'contact';

export interface SoldVehicleLabel {
  name: string;
  kind: SoldVehicleLabelKind;
}

export interface SoldVehicleRowProps {
  vehicle: Vehicle;
  canViewProfit: boolean;
  canViewFinancingPoints: boolean;
  /**
   * Company or contact chips rendered next to the truck title.
   * Prefer this over {@link companies} when kinds differ.
   */
  labels?: SoldVehicleLabel[];
  /** Shorthand for `labels` with `kind: 'company'` (backward compatible). */
  companies?: string[];
}

const labelTooltip = (kind: SoldVehicleLabelKind, name: string): string =>
  kind === 'contact' ? `Sold to contact: ${name}` : `Lead company: ${name}`;

const SoldVehicleRow: React.FC<SoldVehicleRowProps> = ({
  vehicle,
  canViewProfit,
  canViewFinancingPoints,
  labels,
  companies = [],
}) => {
  const theme = useTheme();
  const projectedMileage = getProjectedMileage(vehicle);
  const dealLabel = getDealQualityLabel(vehicle.dealQuality);
  const dealTone = dealColor(dealLabel);
  const soldValue = getVehicleSoldValue(vehicle);

  const dealColorMap: Record<typeof dealTone, string> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    default: theme.palette.text.secondary,
  };
  const dealAccent = dealColorMap[dealTone];

  const displayLabels: SoldVehicleLabel[] =
    labels ??
    companies.map((name) => ({
      name,
      kind: 'company' as const,
    }));

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderLeft: `4px solid ${dealAccent}`,
        transition: 'background-color 150ms ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        },
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            >
              <DirectionsCar />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Unknown truck'}
                </Typography>
                {displayLabels.map(({ name, kind }) => (
                  <Tooltip key={`${kind}-${name}`} title={labelTooltip(kind, name)} arrow>
                    <Chip
                      icon={
                        kind === 'contact' ? (
                          <Person sx={{ fontSize: 14 }} />
                        ) : (
                          <Business sx={{ fontSize: 14 }} />
                        )
                      }
                      label={name}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderRadius: 1.25,
                        bgcolor: alpha(theme.palette.error.main, 0.12),
                        color: theme.palette.error.dark,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                        '& .MuiChip-icon': {
                          color: theme.palette.error.dark,
                          ml: 0.5,
                        },
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap>
                VIN: {vehicle.vin || 'N/A'}
                {vehicle.stock_number ? ` • Stock #${vehicle.stock_number}` : ''}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={6} md={2}>
          <Typography variant="caption" color="text.secondary">
            Sold Date
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {vehicle.sold_date ? new Date(vehicle.sold_date).toLocaleDateString('en-US') : 'N/A'}
          </Typography>
        </Grid>

        <Grid item xs={6} md={2}>
          <Tooltip title="Projected mileage assumes 25,000 mi/year since sold date.">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Mileage (Sold → Projected)
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {typeof vehicle.miles === 'number' ? formatNumber(vehicle.miles) : 'N/A'}
                {projectedMileage !== null && (
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {' '}→ {formatNumber(projectedMileage)}
                  </Box>
                )}
              </Typography>
            </Box>
          </Tooltip>
        </Grid>

        <Grid item xs={6} md={2}>
          <Typography variant="caption" color="text.secondary">
            Sold Value
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: 'success.dark' }}>
            {formatCurrency(soldValue)}
          </Typography>
        </Grid>

        <Grid item xs={6} md={2}>
          <Chip
            size="small"
            icon={
              dealTone === 'success' ? (
                <ThumbUp fontSize="inherit" />
              ) : dealTone === 'error' ? (
                <ThumbDown fontSize="inherit" />
              ) : undefined
            }
            label={`${dealLabel} deal`}
            color={dealTone === 'default' ? undefined : dealTone}
            variant={dealTone === 'default' ? 'outlined' : 'filled'}
            sx={{ fontWeight: 700 }}
          />
        </Grid>
      </Grid>

      {vehicle.finance && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AccountBalance fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.4 }}>
              PAYMENT
            </Typography>
            <Chip
              size="small"
              label={vehicle.finance.payment_type || 'N/A'}
              sx={{
                fontWeight: 600,
                textTransform: 'capitalize',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
              }}
            />
          </Stack>
          <Grid container spacing={1.5}>
            <FinanceCell label="Bank" value={vehicle.finance.bank_name || 'N/A'} />
            <FinanceCell label="Down Payment" value={formatCurrency(vehicle.finance.down_payment)} />
            <FinanceCell label="Amount Financed" value={formatCurrency(vehicle.finance.amount_financed)} />
            <FinanceCell label="Total Sale Price" value={formatCurrency(vehicle.finance.total_sale_price)} />
            {canViewFinancingPoints && (
              <FinanceCell label="Points" value={formatCurrency(vehicle.finance.points)} />
            )}
            {canViewProfit && (
              <FinanceCell
                label="Profit"
                value={formatCurrency(vehicle.finance.profit)}
                valueColor="success.dark"
              />
            )}
          </Grid>
        </>
      )}
    </Paper>
  );
};

interface FinanceCellProps {
  label: string;
  value: React.ReactNode;
  valueColor?: string;
}

const FinanceCell: React.FC<FinanceCellProps> = ({ label, value, valueColor }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ color: valueColor }}>
      {value}
    </Typography>
  </Grid>
);

export default SoldVehicleRow;
